/**
 * What each of §5.1's seven components looks like the moment it is inserted.
 *
 * These are values, not commands, and that is deliberate. `roundtrip.test.ts`
 * runs *these exact objects* through the serializer, so "every component the
 * toolbar can insert survives the round trip" is a property the test checks
 * rather than a claim the toolbar makes. A payload that markdown cannot express
 * fails the suite instead of failing a save, months later, as a 400 the author
 * cannot act on.
 *
 * Two of them exist only because the shapes are load-bearing:
 *
 * - **The table is built here rather than by `insertTable`.** The serializer
 *   refuses a table whose first row is not header cells — "markdown has no
 *   headerless table" — so the header row is not a default anyone may change.
 * - **Cells hold text, not blocks.** `markdown/schema.ts` narrows `tableCell`
 *   and `tableHeader` to `text*`, because a GFM cell carries inline content and
 *   a cell that could hold a list is a cell markdown cannot write.
 *
 * Block ids are absent from every payload. `UniqueID` assigns one as the node
 * enters the document, and the write path assigns any that are still missing
 * (`ensureBlockIds`), so inventing one here would be a third generator of the
 * one value §5.1 hangs anchored comments, block links, and citations off.
 */

import type { CalloutVariant, LiveDataFormat, NodeJSON } from '@matchbox/shared/markdown';

/** A callout, empty and ready to type into. */
export function calloutNode(variant: CalloutVariant = 'note'): NodeJSON {
  return { type: 'callout', attrs: { variant }, content: [{ type: 'paragraph' }] };
}

/** An embed is an atom: the URL is the content (§5.1's `::embed{url=… title=…}`). */
export function embedNode(url: string, title?: string): NodeJSON {
  return { type: 'embed', attrs: { url, title: title?.trim() ? title.trim() : null } };
}

export const DEFAULT_TABLE_ROWS = 3;
export const DEFAULT_TABLE_COLUMNS = 3;

/**
 * A rectangular table with a header row.
 *
 * `colspan` and `rowspan` are written explicitly even though they are the
 * defaults: `prosemirror-tables` computes a table's geometry from them, and a
 * cell that reaches the editing plugin without them breaks column navigation in
 * a way that looks like a rendering bug.
 */
export function tableNode(
  rows: number = DEFAULT_TABLE_ROWS,
  columns: number = DEFAULT_TABLE_COLUMNS,
): NodeJSON {
  const cells = (type: 'tableHeader' | 'tableCell'): NodeJSON[] =>
    Array.from({ length: columns }, () => ({ type, attrs: { colspan: 1, rowspan: 1 } }));

  return {
    type: 'table',
    content: [
      { type: 'tableRow', content: cells('tableHeader') },
      ...Array.from({ length: Math.max(rows - 1, 1) }, () => ({
        type: 'tableRow',
        content: cells('tableCell'),
      })),
    ],
  };
}

/** A task list with one unchecked item — the shape a delivery checklist starts as. */
export function taskListNode(): NodeJSON {
  return {
    type: 'taskList',
    content: [{ type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph' }] }],
  };
}

/** A fenced block. `language` is `null` until the author names one. */
export function codeBlockNode(language: string | null = null): NodeJSON {
  return { type: 'codeBlock', attrs: { language } };
}

/**
 * An image by URL.
 *
 * **An absent description is `null`, not `''`.** Both serialize to `![](…)`, but
 * only `null` comes back from the parser — an empty string makes the insert
 * payload differ from the same image after one round trip, so a document would
 * change shape on its first pass through markdown while looking identical. The
 * round-trip test caught this; the fix belongs here rather than in the parser,
 * because the schema's own default for `alt` is `null` and this was the value
 * disagreeing with it.
 */
export function imageNode(src: string, alt = '', title?: string): NodeJSON {
  return {
    type: 'image',
    attrs: {
      src,
      alt: alt.trim() ? alt : null,
      title: title?.trim() ? title.trim() : null,
    },
  };
}

/**
 * A live-data placeholder — §5.1's "design the syntax now, while nothing uses
 * it, to avoid migrating later".
 *
 * Nothing resolves one in Phase 1; §13 defers the data behind it. Inserting one
 * writes a placeholder that renders as its source name, which is exactly what a
 * document written today should carry so that the resolver, when it arrives,
 * has real call sites to be tested against.
 */
export function liveDataNode(
  source: string,
  format: LiveDataFormat = 'value',
  params: Record<string, string> = {},
): NodeJSON {
  return { type: 'liveData', attrs: { source, format, params } };
}

/**
 * The seven, as the round-trip test enumerates them.
 *
 * A component added to `markdown/schema.ts` without an entry here is a component
 * the editor cannot produce — which is a smaller problem than the reverse, and
 * the reverse is what the test catches.
 */
export const COMPONENT_SAMPLES: ReadonlyArray<{ label: string; node: NodeJSON }> = [
  { label: 'callout', node: calloutNode('warning') },
  { label: 'embed', node: embedNode('https://figma.com/file/abc', 'Homepage v3') },
  { label: 'table', node: tableNode() },
  { label: 'task list', node: taskListNode() },
  { label: 'code', node: codeBlockNode('sql') },
  { label: 'image', node: imageNode('/api/blobs/0198f2c1-0000-7000-8000-000000000000/content') },
  { label: 'live data', node: liveDataNode('project.time_summary', 'table', { range: 'last_30' }) },
];
